<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template>
	<div class="k-cfv-toolbar">
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn
			v-on="on"
			v-show="show_edit_btn"
			x-small
			icon
			color="#666"
			:outlined="viewer.editing_enabled"
			:accessible-label="viewer.editing_enabled ? 'Stop editing framework' : 'Edit framework'"
			@click="toggle_edit"
		><k-icon>fas fa-edit</k-icon></k-btn
		></template
		>{{ viewer.editing_enabled ? 'Stop editing' : 'Edit framework' }}</k-tooltip
		>
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn
			v-on="on"
			v-if="!viewer.embedded_mode"
			x-small
			icon
			color="#666"
			:outlined="(signed_in || viewer.public_review_on) && viewer.show_comments"
			:accessible-label="viewer.show_comments ? 'Hide comments' : 'Show comments'"
			@click="toggle_comments"
		><k-icon>fas fa-comment</k-icon></k-btn
		></template
		>{{ viewer.show_comments ? 'Hide comments' : 'Show comments' }}</k-tooltip
		>
		<k-menu
			:close-on-content-click="false"
			bottom
			nudge-bottom="30""
			nudge-right="200"
			max-height="calc(100vh - 156px)"
			:attach="'.k-cfv-toolbar'"
		>
			<template v-slot:activator="{ on: menu, attrs }">
				<k-tooltip
					bottom
					:attach="'.k-cfv-toolbar'"
				><template v-slot:activator="{ on: tooltip }"
				><k-btn
					v-on="{ ...tooltip, ...menu }"
					v-bind="attrs"
					v-if="show_associations_btn"
					x-small
					icon
					color="#666"
					:outlined="viewer.show_associations"
					:accessible-label="viewer.show_associations ? 'Hide association options' : 'Show association options'"
					@click="viewer.show_associations_btn_clicked"
				><k-icon>fas fa-arrows-alt-h</k-icon></k-btn
				></template
				>Associations Options</k-tooltip
				>
			</template>
			<k-list>
				<k-list-item
					v-if="!viewer.has_associated_frameworks && !viewer.has_copied_items"
					class="m-5"
				><i style="font-size: 15px; color: #666">No associations exist for this framework</i></k-list-item
				>

				<k-list-item
					v-if="
						(viewer.has_associated_frameworks || viewer.has_copied_items) && !viewer.assoc_framework_menu_items_loaded
					"
					class="m-5"
				><i style="font-size: 15px; color: #666">Looking up association information…</i></k-list-item
				>

				<k-list-item
					v-if="viewer.has_associated_frameworks && show_association_framework_list"
					style="align-items: start"
				>
					<k-list-item-content>
						<k-list-item-title class="d-flex">
							<div>Show associations to/from:</div>
							<k-spacer />
							<k-btn
								@click="
									e => {
										viewer.toggle_all_association_frameworks();
									}
								"
								hide-details
								x-small
								text
								color="secondary"
							>{{ !viewer.all_associations_showing ? 'Select All' : 'Select None' }}</k-btn
							>
						</k-list-item-title>

						<k-list-item-subtitle>
							<k-tooltip
								v-for="o in assoc_framework_list"
								v-if="o.fid != 'copies' && (o.afmi.count > 0 || o.afmi.crosswalk)"
								:key="o.fid"
								transition="fade-transition"
								left
							>
								<template v-slot:activator="{ on, attrs }">
									<div
										v-on="on"
										v-bind="attrs"
									>
										<k-checkbox
											v-model="o.afmi.showing"
											@click=""
											hide-details
											dense
											style="max-width: 200px"
										><template v-slot:label
										><b
												v-if="o.afmi.count"
												class="mr-1"
												style="
                            margin-left: -4px;
                            font-size: 10px;
                            background-color: #666;
                            border-radius: 8px;
                            padding: 2px 5px;
                            line-height: 12px;
                            color: #fff;
                          "
											>{{ o.afmi.count }}</b
											>
											<k-icon
												v-show="should_have_crosswalk_menu_item(o)"
												color="primary"
												small
												class="ml-1 mr-2"
												@click.stop="open_crosswalk(o.fid)"
											>fas fa-edit</k-icon
											>
											{{ o.title }}</template
										></k-checkbox
										>
									</div>
								</template>
								<div style="max-width: 150px; text-align: center">{{ o.title }}</div>
							</k-tooltip>
						</k-list-item-subtitle>
					</k-list-item-content>
				</k-list-item>

				<k-list-item
					v-if="!viewer.no_associations_showing && viewer.has_associated_frameworks"
					style="border-top: 1px solid #ccc"
				>
					<k-list-item-content>
						<k-list-item-title class="d-flex">
							<div>Show association types:</div>
							<k-spacer />
							<k-btn
								@click="
									e => {
										viewer.toggle_all_association_types();
									}
								"
								hide-details
								x-small
								text
								color="secondary"
							>{{ !viewer.all_association_types_showing ? 'Select All' : 'Select None' }}</k-btn
							>
						</k-list-item-title>
						<k-list-item-subtitle>
							<k-checkbox
								v-for="(option, name) in viewer.assoc_type_menu_options"
								:key="name"
								v-show="option.used"
								v-model="option.showing"
								@click="viewer.update_associations_to_show"
								hide-details
								dense
							><template v-slot:label
							><b
								v-if="option.count"
								class="mr-1"
								style="
                      margin-left: -4px;
                      font-size: 10px;
                      background-color: #666;
                      border-radius: 8px;
                      padding: 2px 5px;
                      line-height: 12px;
                      color: #fff;
                    "
							>{{ option.count }}</b
							>{{ option.name }}</template
							></k-checkbox
							>
						</k-list-item-subtitle>
					</k-list-item-content>
				</k-list-item>

				<k-list-item
					v-if="viewer.has_copied_items"
					:style="viewer.has_associated_frameworks || viewer.editing_enabled ? 'border-top:1px solid #ccc;' : ''"
				>
					<k-list-item-content style="margin-top: -8px">
						<k-list-item-subtitle>
							<k-checkbox
								v-model="viewer.assoc_framework_menu_items.copies.showing"
								@click="viewer.update_associations_to_show"
								hide-details
								dense
							><template v-slot:label
							><b
								class="mr-1"
								style="
                      margin-left: -4px;
                      font-size: 10px;
                      background-color: #666;
                      border-radius: 8px;
                      padding: 2px 5px;
                      line-height: 12px;
                      color: #fff;
                    "
							>{{ viewer.assoc_framework_menu_items.copies.count }}</b
							>Show originals of copied items</template
							></k-checkbox
							>
						</k-list-item-subtitle>
					</k-list-item-content>
				</k-list-item>
			</k-list>
		</k-menu>
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn
			v-on="on"
			x-small
			icon
			color="#666"
			:outlined="viewer.wrap_item_text"
			:accessible-label="viewer.wrap_item_text ? 'Disable wrapped item text' : 'Enable wrapped item text'"
			@click="toggle_wrap"
		><k-icon style="transform: rotate(90deg); margin-left: -2px; margin-bottom: -2px"
		>fas fa-turn-down</k-icon
		></k-btn
		></template
		>Wrap item text: {{ viewer.wrap_item_text ? 'ON' : 'OFF' }}</k-tooltip
		>
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn
			v-on="on"
			x-small
			icon
			color="#666"
			:class="viewer.open_node_count == 0 ? 'k-cfv-toolbar-disabled' : ''"
			accessible-label="Collapse all items"
			@click="collapse_all"
		><k-icon>fas fa-angles-up</k-icon></k-btn
		></template
		>Collapse all items</k-tooltip
		>
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn
			v-on="on"
			x-small
			icon
			color="#666"
			:outlined="viewer.show_identifiers_in_tiles"
			:accessible-label="viewer.show_identifiers_in_tiles ? 'Hide item identifiers' : 'Show item identifiers'"
			@click="toggle_guids"
		><k-icon>fas fa-id-card</k-icon></k-btn
		></template
		>{{ viewer.show_identifiers_in_tiles ? 'Hide item identifiers' : 'Show item identifiers' }}</k-tooltip
		>
		<!-- <k-tooltip bottom :attach="'.k-cfv-toolbar'"><template v-slot:activator="{on}"><k-btn v-on="on" x-small icon color="#666" @click="show_tiles"><k-icon>fas fa-table-cells-large</k-icon></k-btn></template>Switch to Tile View</k-tooltip> -->
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn
			v-on="on"
			v-if="!$vuetify.breakpoint.xs"
			x-small
			icon
			color="#666"
			accessible-label="Switch to table view"
			@click="show_table"
		><k-icon>fas fa-table-list</k-icon></k-btn
		></template
		>Switch to Table View</k-tooltip
		>
		<k-tooltip
			bottom
			:attach="'.k-cfv-toolbar'"
		><template v-slot:activator="{ on }"
		><k-btn class="k-help-icon-btn" x-small icon color="#666"
			v-on="on"
			accessible-label="Open tree toolbar help"
			@click="show_help"
		><k-icon>fas fa-info-circle</k-icon></k-btn
		></template
		>What’s this?</k-tooltip
		>
	</div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		viewer: { required: true },
		editingControlsAvailable: { type: Boolean, default: true }
	},
	data() {
		return {
			new_crosswalk_menu_showing: false,
		}
	},
	computed: {
		...mapState(['framework_records']),
		...mapGetters(['signed_in']),
		show_edit_btn() {
			return !this.viewer.embedded_mode && this.viewer.user_can_edit && this.editingControlsAvailable
		},
		show_associations_btn() {
			return !this.viewer.hide_associations
		},
		assoc_framework_list() {
			// make a special list of the associated frameworks here, with category abbreviations added and sorted by name
			let arr = []
			for (let fid in this.viewer.assoc_framework_menu_items) {
				if (fid == 'copies') continue

				let fr = this.framework_records.find(x => x.lsdoc_identifier == fid)
				if (!fr) {
					// shouldn't happen
					console.warn('CASEFVToolbar: bad framework record ' + fid)
					continue
				}
				arr.push({
					fid: fid,
					afmi: this.viewer.assoc_framework_menu_items[fid],
					title: fr?.json?.CFDocument?.title || '',
				})
			}
			arr.sort((a, b) => U.natural_sort(a.title, b.title))
			return arr
		},
		show_association_framework_list() {
			for (let o of this.assoc_framework_list) {
				if (o.afmi.count > 0 || o.afmi.crosswalk) {
					return true
				}
			}
			return false
		},
		has_crosswalk_editor_access() {
			// const is_super_user = vapp.is_granted('super')
			// return is_super_user || this.$store.state.site_config.enable_crosswalk_editor_access == 'true'
			return this.$store.state.site_config.enable_crosswalk_editor_access == 'true'
		}
	},
	watch: {},
	created() {},
	mounted() {},
	methods: {
		should_have_crosswalk_menu_item(item) {
			return this.has_crosswalk_editor_access && this.viewer.editing_enabled && item.afmi.crosswalk
		},
		toggle_comments() {
			this.viewer.toggle_show_comments()
		},
		toggle_edit() {
			this.viewer.handle_check_out_for_editing(true)
		},
		collapse_all() {
			this.viewer.collapse_all()
		},
		toggle_wrap() {
			// wrap is always on for a phone-sized screen
			// if (this.$vuetify.breakpoint.xs) return
			this.viewer.wrap_item_text = !this.viewer.wrap_item_text
		},
		toggle_guids() {
			this.viewer.show_identifiers_in_tiles = !this.viewer.show_identifiers_in_tiles
		},
		show_tiles() {
			this.viewer.enter_tile_mode()
		},
		show_table() {
			this.viewer.table_mode_start_node = null
			this.viewer.viewer_mode = 'table'
		},
		show_help() {
			vapp.show_help('tree_toolbar')
		},
		open_crosswalk(lsdoc_identifier) {
			vapp.framework_list_component.open_crosswalk([this.viewer.framework_record.lsdoc_identifier, lsdoc_identifier])
		},
		crosswalk_edit_btn_clicked() {
			if (!this.$store.state.crosswalk_warning_issued) {
				this.$confirm({
					// title: 'Are you a witch?',
					text: `Note that the Crosswalk Editor is currently a <b>BETA</b> feature in ${this.$store.state.site_config.app_name}. Using this editor may cause unintended consequences.`,
					acceptText: 'Enable Beta Crosswalk Editor',
					// cancelText: 'I sink',
					dialogMaxWidth: 500
					// focusBtn: true,		// focus on the accept btn when dialog is rendered
				})
					.then(y => {
						this.$store.commit('set', ['crosswalk_warning_issued', true])
						this.crosswalk_edit_btn_clicked()
					})
					.catch(n => {
						console.log(n)
					})
					.finally(f => {})
				return
			}
			this.new_crosswalk_menu_showing = !this.new_crosswalk_menu_showing
		}
	}
}
</script>

<style lang="scss">
.k-cfv-toolbar {
  position: absolute;
  left: 52px;
  top: 12px;
  z-index: 1;
  text-align: left;
  background-color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid #ccc;
  height: 28px;
  line-height: 22px;
  text-align: center;
  padding: 2px 4px;
  border-radius: 10px 10px 0 0;
  pointer-events: auto;

  .v-btn--icon.v-size--x-small {
    width: 22px;
    height: 22px;
  }

  .v-btn {
    margin-left: 2px;
    margin-right: 2px;
    .fas,
    .far {
      font-size: 14px !important;
    }
    .fa-id-card {
      font-size: 13px !important;
    }

    .fa-info-circle {
      font-size: 16px !important;
      color: $v-light-blue !important;
    }
  }

  .k-cfv-toolbar-disabled {
    .fas,
    .far {
      color: #666 !important;
    }
  }

  .v-btn--outlined {
    border-color: #000 !important;
    .fas,
    .far {
      color: #000 !important;
    }
  }
}
</style>
